/**
 * Destinations API Service
 * Handles fetching rich destination details and sub-components and AI-generated info.
 */
export class DestinationsService {
  constructor(apiClient, handleApiError) {
    this.apiClient = apiClient;
    this.handleApiError = handleApiError;
  }

  /**
   * Get basic destination info by slug
   * @param {string} slug - The destination slug (e.g., 'sydney-australia')
   * @returns {Promise<Object>}
   */
  async getDestinationBasicInfo(slug) {
    try {
      const response = await this.apiClient.get(`/destinations/${slug}/basic-info`);
      return response.data;
    } catch (error) {
      this.handleApiError(error, `Failed to fetch basic info for ${slug}`);
      throw error;
    }
  }

  /**
   * Get a destination image from Google Places (public, no auth required)
   * @param {string} destination - Destination name
   * @param {string|null} country - Optional country name
   * @returns {Promise<{image_url: string}>}
   */
  async getDestinationImage(destination, country = null) {
    try {
      const params = { destination };
      if (country) params.country = country;
      const response = await this.apiClient.get('/destinations/image', { params });
      return response.data;
    } catch (error) {
      this.handleApiError(error, `Failed to fetch image for ${destination}`);
      throw error;
    }
  }

  /**
   * Get full destination details (with AI estimates for initial load)
   * @param {string} slug - Destination slug
   * @returns {Promise<Object>}
   */
  async getDestinationDetails(slug) {
    try {
      const response = await this.apiClient.get(`/destinations/${slug}`);
      return response.data;
    } catch (error) {
      this.handleApiError(error, `Failed to fetch details for ${slug}`);
      throw error;
    }
  }

  /**
   * Get best time to visit and weather patterns (AI-generated)
   * @param {string} slug - Destination slug
   * @returns {Promise<Object>}
   */
  async getBestTimeToVisit(slug) {
    try {
      const response = await this.apiClient.get(`/destinations/${slug}/best-time-to-visit`);
      return response.data;
    } catch (error) {
      this.handleApiError(error, `Failed to fetch best time to visit for ${slug}`);
      throw error;
    }
  }

  /**
   * Get real-time accommodations (Booking.com)
   * @param {string} slug - Destination slug
   * @param {string} [currency='USD'] - Currency code
   * @returns {Promise<Array>}
   */
  async getAccommodations(slug, currency = 'USD') {
    try {
      const response = await this.apiClient.get(`/destinations/${slug}/accommodations`, {
        params: { currency }
      });
      return response.data;
    } catch (error) {
      this.handleApiError(error, `Failed to fetch accommodations for ${slug}`);
      throw error;
    }
  }

  /**
   * Get real-time restaurants (Google Places)
   * @param {string} slug - Destination slug
   * @param {string} [currency='USD'] - Currency code
   * @returns {Promise<Array>}
   */
  async getRestaurants(slug, currency = 'USD') {
    try {
      const response = await this.apiClient.get(`/destinations/${slug}/restaurants`, {
        params: { currency }
      });
      return response.data;
    } catch (error) {
      this.handleApiError(error, `Failed to fetch restaurants for ${slug}`);
      throw error;
    }
  }

  /**
   * Get real-time attractions (Google Places)
   * @param {string} slug - Destination slug
   * @returns {Promise<Array>}
   */
  async getAttractions(slug) {
    try {
      const response = await this.apiClient.get(`/destinations/${slug}/attractions`);
      return response.data;
    } catch (error) {
      this.handleApiError(error, `Failed to fetch attractions for ${slug}`);
      throw error;
    }
  }

  /**
   * Get travel logistics (Amadeus)
   * @param {string} slug - Destination slug
   * @param {string} fromCity - Origin city (defaults to 'Sydney' for now)
   * @returns {Promise<Object>}
   */
  async getHowToGetThere(slug, fromCity = 'Sydney') {
    try {
      const response = await this.apiClient.get(`/destinations/${slug}/how-to-get-there`, {
        params: { from_city: fromCity }
      });
      return response.data;
    } catch (error) {
      this.handleApiError(error, `Failed to fetch transport options for ${slug}`);
      throw error;
    }
  }

  /**
   * Get destination features/tags (AI-generated with caching)
   * @param {string} slug - Destination slug
   * @returns {Promise<Array>} - Array of feature objects with 'name' key
   */
  async getFeatures(slug) {
    try {
      const response = await this.apiClient.get(`/destinations/${slug}/features`);
      return response.data;
    } catch (error) {
      this.handleApiError(error, `Failed to fetch features for ${slug}`);
      throw error;
    }
  }

  /**
   * Post a destination seed (recommendation story context) for a slug.
   * This is best-effort — failures must never block navigation.
   * @param {string} slug - Destination slug
   * @param {Object} payload - Seed data (card context from TripFinder results)
   * @returns {Promise<Object|null>} Seed data or null on failure
   */
  async postSeed(slug, payload) {
    try {
      const response = await this.apiClient.post(`/destinations/${slug}/seed`, payload);
      return response.data;
    } catch (error) {
      // Non-critical — seed is best-effort
      console.warn('Failed to post seed:', error);
      return null;
    }
  }

  /**
   * Get a previously posted destination seed by slug.
   * Returns null on 404 (no seed exists yet) or on any error.
   * @param {string} slug - Destination slug
   * @returns {Promise<Object|null>} Seed data or null
   */
  async getSeed(slug) {
    try {
      const response = await this.apiClient.get(`/destinations/${slug}/seed`);
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) return null;
      console.warn('Failed to get seed:', error);
      return null;
    }
  }
}